import { useEffect, useMemo, useRef, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import {
  ChevronLeft, Loader2, Pencil, Trash2, Upload, MoreHorizontal,
  Plus, X, Video, Image as ImageIcon, Clapperboard, Hand, MousePointerClick,
  Volume2, VolumeX, Clock, ArrowRight, Save,
} from 'lucide-react'
import { Button } from '@meno/ui/components/ui/button'
import { Badge } from '@meno/ui/components/ui/badge'
import { ConfirmDialog } from '@meno/ui/components/ui/confirm-dialog'
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from '@meno/ui/components/ui/dropdown-menu'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@meno/ui/components/ui/tabs'
import { TextInput } from '@meno/ui/components/shared/TextInput'
import { ColorInput } from '@meno/ui/components/shared/ColorInput'
import {
  getStopScreen, updateStopScreen, deleteStopScreen, publishStopScreen, BACKEND_URL,
} from '@meno/ui/lib/api'
import { usePageHeader } from '@meno/ui/components/layout/PageHeader'
import { formatDate } from '@meno/ui/lib/utils'
import PlaylistContentPicker, { type PickedItem } from '@/components/PlaylistContentPicker'
import { PublishDialog } from '@/components/PublishDialog'
import DurationInput from '@/components/DurationInput'
import PreviewPanel, { type PreviewItem } from '@/components/ContentPreview'

type MediaFile = { id: number; original_name: string; file_type: string; url: string; duration?: number }
type PlaylistRef = { id: number; name: string }
type StopScreenData = {
  id: number; name: string
  dwell_seconds: number; show_cta: boolean; cta_text: string; cta_position: string; touch_mode: string
  cta_variant: string; cta_style: string; cta_text_color: string; cta_bg_color: string; cta_size: string
  media: MediaFile[]
  media_durations: Record<number, number>
  media_order: Record<number, number>
  media_muted: Record<number, boolean>
  playlists: PlaylistRef[]
  playlist_order: Record<number, number>
  screens: { id: number; name: string }[]
  created_at: string
}
// Behaviour + CTA fields, buffered locally and committed in one PATCH by the
// Save button — everything else on this page (content picks, rename) still
// auto-saves immediately, but this group has enough interdependent chips
// (dwell, dismiss mode, text, position, appearance ×5) that patching on every
// click made each tweak seem to invisibly go live with no confirmation.
type CtaFields = Pick<
  StopScreenData,
  'dwell_seconds' | 'show_cta' | 'cta_text' | 'cta_position' | 'touch_mode'
  | 'cta_variant' | 'cta_style' | 'cta_text_color' | 'cta_bg_color' | 'cta_size'
>
const ctaFieldsOf = (d: StopScreenData): CtaFields => ({
  dwell_seconds: d.dwell_seconds, show_cta: d.show_cta, cta_text: d.cta_text,
  cta_position: d.cta_position, touch_mode: d.touch_mode, cta_variant: d.cta_variant,
  cta_style: d.cta_style, cta_text_color: d.cta_text_color, cta_bg_color: d.cta_bg_color,
  cta_size: d.cta_size,
})

const THUMB_CACHE_PREFIX = 'meno_vthumb_'
const DWELL_CHOICES = [15, 20, 25, 30, 35, 40, 45]
// 3×3 CTA position grid — values match the backend CTA_POSITIONS set.
const CTA_GRID: string[][] = [
  ['top-left', 'top-center', 'top-right'],
  ['center-left', 'center', 'center-right'],
  ['bottom-left', 'bottom-center', 'bottom-right'],
]

function mediaUrl(m: { url: string }) {
  return m.url.startsWith('http') ? m.url : `${BACKEND_URL}${m.url}`
}

// CTA button appearance options — keys match the backend CTA_VARIANTS /
// CTA_STYLES / CTA_SIZES sets. Labels are translated at render time.
const CTA_VARIANTS = ['button', 'pill', 'circle', 'hand', 'arrow'] as const
const CTA_STYLES   = ['solid', 'glassy', 'gradient', 'outline'] as const
const CTA_SIZES    = ['xs', 'sm', 'md', 'lg', 'xl'] as const
const CTA_SIZE_LABEL: Record<(typeof CTA_SIZES)[number], string> = {
  xs: 'Extra small', sm: 'Small', md: 'Medium', lg: 'Large', xl: 'Extra large',
}

// Mirror of the webplayer's colour helpers so the editor preview matches the
// device exactly. #RGB / #RRGGBB / #RRGGBBAA → {r,g,b}.
function hexRgb(hex: string) {
  let h = (hex || '').replace('#', '')
  if (h.length === 3) h = h.split('').map(c => c + c).join('')
  if (h.length === 8) h = h.slice(0, 6)
  const n = parseInt(h || 'ffffff', 16)
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 }
}
const rgba = (hex: string, a: number) => { const { r, g, b } = hexRgb(hex); return `rgba(${r},${g},${b},${a})` }
const darken = (hex: string, amt: number) => {
  const { r, g, b } = hexRgb(hex); const f = (v: number) => Math.round(v * (1 - amt))
  return `rgb(${f(r)},${f(g)},${f(b)})`
}
function ctaFillStyle(style: string, bg: string, fg: string): React.CSSProperties {
  switch (style) {
    // Border in em so the outline scales with the button's font size.
    case 'solid':    return { background: bg, color: fg }
    case 'gradient': return { background: `linear-gradient(135deg, ${bg}, ${darken(bg, 0.4)})`, color: fg }
    case 'outline':  return { background: 'transparent', color: fg, border: `0.08em solid ${bg}` }
    // Genuinely glassy: low fill opacity + a strong blur so the busy content
    // behind actually shows through (frosted), plus a light edge highlight —
    // the standard glassmorphism cue that reads as "glass" rather than just
    // "translucent". The old 0.85 opacity / 8px blur was so close to opaque
    // the blur was barely perceptible; this is the exact formula the
    // webplayer/Android renderers use so preview and live playback match.
    default:         return {
      background: rgba(bg, 0.3),
      color: fg,
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      border: '0.06em solid rgba(255,255,255,0.35)',
    }
  }
}
// CTA position → absolute-position classes inside the preview frame (mirror
// of the webplayer's CTA_POS_CLASS so the preview matches the device).
const CTA_POS_CLASS: Record<string, string> = {
  'top-left': 'top-[6%] start-[6%]',
  'top-center': 'top-[6%] start-1/2 -translate-x-1/2',
  'top-right': 'top-[6%] end-[6%]',
  'center-left': 'top-1/2 -translate-y-1/2 start-[6%]',
  'center': 'top-1/2 start-1/2 -translate-x-1/2 -translate-y-1/2',
  'center-right': 'top-1/2 -translate-y-1/2 end-[6%]',
  'bottom-left': 'bottom-[6%] start-[6%]',
  'bottom-center': 'bottom-[6%] start-1/2 -translate-x-1/2',
  'bottom-right': 'bottom-[6%] end-[6%]',
}
// Font size in container-min units so the button scales with the preview
// frame — and, critically, with the ACTUAL screen, since the webplayer/
// Android renderers use this exact same cqmin scale on the real device (see
// StopScreenOverlay.tsx's SIZE_CQMIN). Before, the device sized the button in
// fixed px regardless of the screen's resolution while this preview scaled
// relative to its own frame, so the two could only agree by coincidence at
// one specific screen size — this is what made the preview inaccurate. All
// paddings/gaps/icons on the button are in em, so everything follows the
// same scale.
const CTA_PREVIEW_FONT: Record<string, string> = {
  xs: '2.4cqmin',
  sm: '3.0cqmin',
  md: '3.6cqmin',
  lg: '4.4cqmin',
  xl: '5.4cqmin',
}

// Chip — shared pill toggle used by the appearance option rows.
function Chip({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button type="button" onClick={onClick}
      className={`px-3 h-8 rounded-lg text-xs font-medium border capitalize transition-colors ${
        active ? 'bg-primary text-primary-foreground border-primary' : 'border-border text-muted-foreground hover:text-foreground hover:bg-muted/40'
      }`}>
      {children}
    </button>
  )
}

// Live CTA preview — renders the button exactly as the webplayer overlay
// will, painted onto the screen preview frame at its configured position and
// scaled to the frame via container-query units. Takes the DRAFT (unsaved)
// fields so the preview reflects what the operator is currently configuring,
// not just the last-saved state.
function CtaOverlay({ data }: { data: CtaFields }) {
  const variant = data.cta_variant || 'pill'
  const shapeCls =
    variant === 'button' ? 'rounded-[0.5em]'
    : variant === 'circle' ? 'rounded-full aspect-square justify-center !px-0 min-w-[2.6em]'
    : 'rounded-full'
  const posCls = CTA_POS_CLASS[data.cta_position] ?? CTA_POS_CLASS.center
  return (
    // Pinned LTR: the position classes below use start/end logical
    // properties to mirror the webplayer's own CTA_POS_CLASS exactly, but
    // the real device is never RTL-aware — "top-left" always renders at the
    // literal top-left regardless of the dashboard's language. Without this,
    // an Arabic-language operator saw the button on the wrong side of the
    // preview vs. where it actually shows on screen. Same fix as
    // ZonePreviewFrame / LayoutThumb's diagrams.
    <div
      dir="ltr"
      className="absolute inset-0 pointer-events-none z-10"
      style={{ containerType: 'size' } as React.CSSProperties}
      aria-hidden
    >
      <span
        style={{
          ...ctaFillStyle(data.cta_style, data.cta_bg_color, data.cta_text_color),
          fontSize: CTA_PREVIEW_FONT[data.cta_size] ?? CTA_PREVIEW_FONT.lg,
        }}
        className={`absolute ${posCls} inline-flex items-center px-[1em] py-[0.5em] gap-[0.4em] ${shapeCls} font-semibold shadow-xl`}
      >
        {variant === 'hand' && <Hand className="w-[1em] h-[1em] shrink-0" />}
        {data.cta_text && <span className="whitespace-nowrap">{data.cta_text}</span>}
        {variant === 'arrow' && <ArrowRight className="w-[1em] h-[1em] shrink-0" />}
      </span>
    </div>
  )
}

export default function StopScreenDetailsPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { t, i18n } = useTranslation()
  const isRTL = i18n.language === 'ar'
  // Always ChevronLeft — a global CSS rule mirrors it under dir="rtl"; see
  // WizardNavButton.tsx for why swapping it here too double-flips it back.
  const ChevronIcon = ChevronLeft

  const [data, setData] = useState<StopScreenData | null>(null)
  const [loading, setLoading] = useState(true)
  const [pickerOpen, setPickerOpen] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [publishMode, setPublishMode] = useState<'publish' | 'schedule' | null>(null)
  const [editingTitle, setEditingTitle] = useState(false)
  const [titleDraft, setTitleDraft] = useState('')

  // Behaviour/CTA draft — buffered locally, committed as one PATCH by the
  // Save button. Seeded from `data` only on the initial load (the ref guard),
  // never on a later `data` update from an UNRELATED patch (content picks,
  // rename) — those would otherwise silently blow away an in-progress,
  // unsaved CTA edit.
  const [ctaDraft, setCtaDraft] = useState<CtaFields | null>(null)
  const [savedCtaSig, setSavedCtaSig] = useState('')
  const [savingCta, setSavingCta] = useState(false)
  const ctaInitializedRef = useRef(false)
  const ctaDirty = !!ctaDraft && JSON.stringify(ctaDraft) !== savedCtaSig

  useEffect(() => {
    if (!id) return
    getStopScreen(Number(id))
      .then(r => setData(r.data))
      .catch(() => { toast.error(t('stopScreen.loadFailed', 'Failed to load stop screen')); navigate('/content-hub') })
      .finally(() => setLoading(false))
  }, [id, navigate, t])

  useEffect(() => {
    if (!data || ctaInitializedRef.current) return
    ctaInitializedRef.current = true
    const fields = ctaFieldsOf(data)
    setCtaDraft(fields)
    setSavedCtaSig(JSON.stringify(fields))
  }, [data])

  const saveCta = async () => {
    if (!data || !ctaDraft || savingCta) return
    setSavingCta(true)
    try {
      const { data: updated } = await updateStopScreen(data.id, ctaDraft)
      setData(updated)
      setSavedCtaSig(JSON.stringify(ctaDraft))
      toast.success(t('common.saved', 'Settings saved'))
    } catch {
      toast.error(t('common.updateFailed', 'Failed to update'))
    } finally {
      setSavingCta(false)
    }
  }

  const mediaRows = useMemo(() => {
    if (!data) return []
    return [...data.media].sort((a, b) => (data.media_order[a.id] ?? 0) - (data.media_order[b.id] ?? 0))
  }, [data])
  const playlistRows = useMemo(() => {
    if (!data) return []
    return [...data.playlists].sort((a, b) => (data.playlist_order[a.id] ?? 0) - (data.playlist_order[b.id] ?? 0))
  }, [data])

  // ── Persistence ─────────────────────────────────────────────────────────
  const patch = async (body: Record<string, unknown>) => {
    if (!data) return
    try {
      const { data: updated } = await updateStopScreen(data.id, body)
      setData(updated)
    } catch {
      toast.error(t('common.updateFailed', 'Failed to update'))
    }
  }

  // Rebuild the full media + playlist maps from the current row order and PATCH.
  const persistContent = async (media: MediaFile[], playlists: PlaylistRef[], durations?: Record<number, number>, muted?: Record<number, boolean>) => {
    if (!data) return
    const media_ids = media.map(m => m.id)
    const media_order: Record<number, number> = {}
    const media_durations: Record<number, number> = { ...data.media_durations, ...(durations || {}) }
    const media_muted: Record<number, boolean> = { ...data.media_muted, ...(muted || {}) }
    media.forEach((m, i) => { media_order[m.id] = i })
    const playlist_ids = playlists.map(p => p.id)
    const playlist_order: Record<number, number> = {}
    playlists.forEach((p, i) => { playlist_order[p.id] = i })
    await patch({ media_ids, media_order, media_durations, media_muted, playlist_ids, playlist_order })
  }

  // The picker runs in `removable` mode → `picked` is the full desired set.
  const handleSetContent = async (picked: PickedItem[]) => {
    setPickerOpen(false)
    if (!data) return
    const pickedMedia = picked.filter((p): p is Extract<PickedItem, { kind: 'media' }> => p.kind === 'media')
      .filter(p => p.file_type.startsWith('image') || p.file_type.startsWith('video'))
    const pickedPlaylists = picked.filter((p): p is Extract<PickedItem, { kind: 'playlist' }> => p.kind === 'playlist')

    // Retain existing rows still selected (preserve order), then append new.
    const keepMediaIds = new Set(pickedMedia.map(p => p.id))
    const keepPlIds = new Set(pickedPlaylists.map(p => p.id))
    const retainedMedia = mediaRows.filter(m => keepMediaIds.has(m.id))
    const existingMediaIds = new Set(mediaRows.map(m => m.id))
    const newMedia: MediaFile[] = pickedMedia
      .filter(p => !existingMediaIds.has(p.id))
      .map(p => ({ id: p.id, original_name: p.original_name, file_type: p.file_type, url: p.url, duration: p.duration }))
    const retainedPl = playlistRows.filter(p => keepPlIds.has(p.id))
    const existingPlIds = new Set(playlistRows.map(p => p.id))
    const newPl: PlaylistRef[] = pickedPlaylists.filter(p => !existingPlIds.has(p.id)).map(p => ({ id: p.id, name: p.name }))

    await persistContent([...retainedMedia, ...newMedia], [...retainedPl, ...newPl])
    toast.success(t('stopScreen.contentUpdated', 'Stop screen updated'))
  }

  const removeMedia = (mid: number) => persistContent(mediaRows.filter(m => m.id !== mid), playlistRows)
  const removePlaylist = (pid: number) => persistContent(mediaRows, playlistRows.filter(p => p.id !== pid))
  const setDuration = (mid: number, s: number) => persistContent(mediaRows, playlistRows, { [mid]: s })
  const toggleMute = (mid: number) => {
    if (!data) return
    persistContent(mediaRows, playlistRows, undefined, { [mid]: !data.media_muted[mid] })
  }

  const commitTitle = async () => {
    if (!data) return
    const next = titleDraft.trim()
    setEditingTitle(false)
    if (!next || next === data.name) return
    await patch({ name: next })
    toast.success(t('common.renamedSuccess', 'Renamed successfully'))
  }

  const handleDelete = async () => {
    if (!data) return
    try {
      await deleteStopScreen(data.id)
      toast.success(t('common.deleted', 'Deleted'))
      navigate('/content-hub')
    } catch {
      toast.error(t('common.updateFailed', 'Failed to update'))
    }
    setDeleteOpen(false)
  }

  // Duration used both for the content-list inputs and the preview mapping.
  const durationOf = (m: MediaFile) =>
    Math.max(1, data?.media_durations[m.id] ?? (m.file_type.startsWith('video') ? (m.duration ?? 10) : 10))

  // ── Header ────────────────────────────────────────────────────────────────
  const infoBar = useMemo(() => {
    if (!data) return null
    return (
      <div className="flex items-center justify-between gap-3 w-full ps-2 pe-3">
        <div className="flex items-center gap-3 min-w-0">
          <button type="button" onClick={() => navigate(-1)} aria-label={t('common.back', 'Back')}
            className="w-9 h-9 flex items-center justify-center rounded-md text-foreground/70 hover:text-foreground hover:bg-muted transition-colors shrink-0">
            <ChevronIcon className="w-4 h-4" />
          </button>
          <div className="w-10 h-10 rounded-xl bg-stop-screen/15 flex items-center justify-center shrink-0">
            <Hand className="w-5 h-5 text-stop-screen" />
          </div>
          <div className="min-w-0 flex flex-col">
            <div className="flex items-center gap-1.5 min-w-0">
              {editingTitle ? (
                <input autoFocus value={titleDraft} onChange={e => setTitleDraft(e.target.value)} onBlur={commitTitle}
                  onKeyDown={e => { if (e.key === 'Enter') commitTitle(); if (e.key === 'Escape') { setEditingTitle(false) } }}
                  maxLength={150}
                  className="text-[15px] font-semibold leading-tight bg-transparent border-b border-primary outline-none min-w-0 w-48" />
              ) : (
                <>
                  <h1 className="text-[15px] font-semibold text-foreground leading-tight truncate">{data.name}</h1>
                  <button type="button" onClick={() => { setTitleDraft(data.name); setEditingTitle(true) }}
                    aria-label={t('common.rename', 'Rename')}
                    className="w-6 h-6 flex items-center justify-center rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors shrink-0">
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                </>
              )}
            </div>
            <p className="text-[11px] text-muted-foreground leading-tight truncate">
              {t('playlist.addedOn', 'Added on')} {formatDate(data.created_at, i18n.language)}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button onClick={() => setPublishMode('publish')} className="gap-2 h-9">
            <Upload className="w-4 h-4" />{t('playlist.publish', 'Publish')}
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger aria-label={t('common.more', 'More')}
              className="w-9 h-9 flex items-center justify-center rounded-md border border-border bg-muted/40 text-foreground hover:bg-muted transition-colors">
              <MoreHorizontal className="w-4 h-4" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-44">
              <DropdownMenuItem onClick={() => setPublishMode('schedule')} className="gap-2">
                <Clock className="w-4 h-4" />{t('playlist.schedule', 'Schedule')}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setDeleteOpen(true)} className="gap-2 text-destructive focus:text-destructive">
                <Trash2 className="w-4 h-4" />{t('common.delete', 'Delete')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, editingTitle, titleDraft, i18n.language])

  usePageHeader(infoBar ? { render: infoBar } : { title: t('contentHub.tabs.stopScreens', 'Stop Screens') })

  if (loading) return <div className="flex h-64 items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-muted-foreground" /></div>
  if (!data) return null

  // Attract media → the shared preview (images / videos), same cycling player
  // the playlist page uses. Only media cycles in the preview (not playlists).
  const attractItems: PreviewItem[] = mediaRows.map(m => {
    const isVideo = m.file_type.startsWith('video')
    return {
      key: `m-${m.id}`,
      name: m.original_name,
      kind: isVideo ? 'video' : 'image',
      url: m.url,
      durationSec: durationOf(m),
      muted: !!data.media_muted[m.id],
      thumb: isVideo ? (localStorage.getItem(`${THUMB_CACHE_PREFIX}${m.id}`) ?? undefined) : undefined,
    }
  })

  return (
    <Tabs defaultValue="content" dir={isRTL ? 'rtl' : 'ltr'} className="-m-4 sm:-m-[var(--space-l)] p-4 sm:p-[var(--space-l)] h-[calc(100dvh-3.5rem-1px)] flex flex-col bg-background">
      {/* Two distinct cards on the canvas — the content/settings editor and the
          preview each read as their own bordered, rounded card with a gutter
          between them (mirrors the screen page's Content | Preview split). */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 flex-1 min-h-0">

        {/* ── Left — content + settings (tabbed, like the screen page) ── */}
        <div className="flex flex-col min-h-0 bg-card rounded-2xl border border-border shadow-sm overflow-hidden">
          <div className="border-b border-border px-6 shrink-0">
            <TabsList className="h-11 bg-transparent p-0 rounded-none gap-1">
              <TabsTrigger value="content" className="h-11 rounded-none border-b-2 border-transparent bg-transparent px-4 text-sm font-medium text-muted-foreground data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:text-foreground data-[state=active]:shadow-none">
                {t('stopScreen.contentTab', 'Content')}
              </TabsTrigger>
              <TabsTrigger value="settings" className="h-11 rounded-none border-b-2 border-transparent bg-transparent px-4 text-sm font-medium text-muted-foreground data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:text-foreground data-[state=active]:shadow-none">
                {t('stopScreen.settingsTab', 'Settings')}
              </TabsTrigger>
            </TabsList>
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto p-6">
            <TabsContent value="content" forceMount className="mt-0 data-[state=inactive]:hidden">
          <div className="mb-3 inline-flex self-start items-center gap-1.5 text-sm font-medium">
            <Clapperboard className="w-4 h-4" />{t('stopScreen.content', 'Attract content')}
            <Badge variant="secondary" className="ml-1 h-4 px-1 text-[10px]">{mediaRows.length + playlistRows.length}</Badge>
          </div>

          {mediaRows.length + playlistRows.length === 0 ? (
            <div className="rounded-xl border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
              {t('stopScreen.emptyContent', 'Add images, videos, or image/video playlists to show while the screen is idle.')}
            </div>
          ) : (
            <ul className="space-y-2">
              {mediaRows.map((m, i) => {
                const isVideo = m.file_type.startsWith('video')
                const isImage = m.file_type.startsWith('image')
                const thumb = isVideo ? localStorage.getItem(`${THUMB_CACHE_PREFIX}${m.id}`) : null
                return (
                  <li key={`m-${m.id}`} className="group flex items-center gap-3 p-3 rounded-xl border border-border/60 bg-muted/40">
                    <span className="text-xs text-muted-foreground font-mono w-5 text-center shrink-0">{i + 1}</span>
                    <div className="w-16 h-10 rounded-lg overflow-hidden bg-muted shrink-0 border border-border/40 flex items-center justify-center">
                      {isImage && <img src={mediaUrl(m)} alt="" className="w-full h-full object-cover" loading="lazy" />}
                      {isVideo && thumb && <img src={thumb} alt="" className="w-full h-full object-cover" />}
                      {isVideo && !thumb && <Video className="w-4 h-4 text-purple-400" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{m.original_name}</p>
                      <Badge variant="secondary" className={`mt-0.5 text-[9px] gap-0.5 py-0 h-4 border-0 ${isVideo ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400' : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'}`}>
                        {isVideo ? <Video className="w-2 h-2" /> : <ImageIcon className="w-2 h-2" />} {isVideo ? t('common.video', 'Video') : t('common.image', 'Image')}
                      </Badge>
                    </div>
                    {isVideo ? (
                      <button type="button" onClick={() => toggleMute(m.id)} aria-label={t('playlist.rowMute', 'Mute video')}
                        className={`w-7 h-7 flex items-center justify-center rounded-md shrink-0 ${data.media_muted[m.id] ? 'text-amber-600' : 'text-muted-foreground hover:text-foreground hover:bg-muted'}`}>
                        {data.media_muted[m.id] ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
                      </button>
                    ) : (
                      <DurationInput value={durationOf(m)} onCommit={s => setDuration(m.id, s)} ariaLabel={t('playlist.rowDurationAria', 'Duration in seconds')} />
                    )}
                    <button type="button" onClick={() => removeMedia(m.id)} aria-label={t('common.remove', 'Remove')}
                      className="w-7 h-7 max-md:w-10 max-md:h-10 flex items-center justify-center rounded-md text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors shrink-0 opacity-0 group-hover:opacity-100 touch-reveal">
                      <X className="w-4 h-4" />
                    </button>
                  </li>
                )
              })}
              {playlistRows.map((p, i) => (
                <li key={`p-${p.id}`} className="group flex items-center gap-3 p-3 rounded-xl border border-border/60 bg-muted/40">
                  <span className="text-xs text-muted-foreground font-mono w-5 text-center shrink-0">{mediaRows.length + i + 1}</span>
                  <div className="w-16 h-10 rounded-lg bg-teal-500/10 shrink-0 border border-border/40 flex items-center justify-center">
                    <Clapperboard className="w-4 h-4 text-teal-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{p.name}</p>
                    <Badge variant="secondary" className="mt-0.5 text-[9px] gap-0.5 py-0 h-4 border-0 bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400">
                      <Clapperboard className="w-2 h-2" /> {t('contentHub.tabs.playlists', 'Playlist')}
                    </Badge>
                  </div>
                  <button type="button" onClick={() => removePlaylist(p.id)} aria-label={t('common.remove', 'Remove')}
                    className="w-7 h-7 max-md:w-10 max-md:h-10 flex items-center justify-center rounded-md text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors shrink-0 opacity-0 group-hover:opacity-100 touch-reveal">
                    <X className="w-4 h-4" />
                  </button>
                </li>
              ))}
            </ul>
          )}

          <div className="pt-4 mt-4 flex justify-center">
            <Button onClick={() => setPickerOpen(true)} className="gap-2 h-11 px-6 bg-white text-foreground hover:bg-muted border border-border">
              <Plus className="w-4 h-4" />{t('playlist.addContent', 'Add Content')}
            </Button>
          </div>
            </TabsContent>

            <TabsContent value="settings" forceMount className="mt-0 data-[state=inactive]:hidden">
          {/* Behaviour — moved into its own Settings tab. Buffered: every chip/
              input below edits `cta` (the local draft) only — nothing reaches
              the server until Save, so a run of tweaks reads as one deliberate
              change instead of N invisible live ones. */}
          {ctaDraft && (() => {
            const cta = ctaDraft
            const set = (patch: Partial<CtaFields>) => setCtaDraft(prev => prev ? { ...prev, ...patch } : prev)
            return (
          <div className="rounded-xl border border-border/60 bg-muted/40 p-5 space-y-5">
            <div className="flex items-center justify-between gap-3">
              <h3 className="text-base font-semibold">{t('stopScreen.behaviour', 'Behaviour')}</h3>
              <Button size="sm" onClick={saveCta} disabled={!ctaDirty || savingCta} className="gap-1.5 h-8">
                {savingCta ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                {t('common.save', 'Save')}
              </Button>
            </div>

            <div className="space-y-2">
              <p className="text-sm font-medium">{t('stopScreen.dwell', 'Stay dismissed for')}</p>
              <p className="text-xs text-muted-foreground">{t('stopScreen.dwellHint', 'After a touch, the overlay hides for this long before returning.')}</p>
              <div className="flex flex-wrap gap-1.5">
                {DWELL_CHOICES.map(s => (
                  <button key={s} type="button" onClick={() => set({ dwell_seconds: s })}
                    className={`px-3 h-8 rounded-lg text-xs font-medium border transition-colors ${cta.dwell_seconds === s ? 'bg-primary text-primary-foreground border-primary' : 'border-border text-muted-foreground hover:text-foreground hover:bg-muted/40'}`}>
                    {s}s
                  </button>
                ))}
              </div>
            </div>

            {/* Dismiss mode — a single choice. "Touch Anywhere" makes the whole
                screen the touch target (no button, no position). "Button" shows
                a tappable prompt at a chosen position. */}
            <div className="space-y-2">
              <p className="text-sm font-medium">{t('stopScreen.interaction', 'Dismiss by')}</p>
              <div className="grid grid-cols-2 gap-2">
                <button type="button" onClick={() => set({ show_cta: false, touch_mode: 'anywhere' })}
                  className={`h-11 rounded-xl border text-sm font-medium flex items-center justify-center gap-2 transition-colors ${!cta.show_cta ? 'bg-foreground text-background border-foreground' : 'border-border text-muted-foreground hover:bg-muted/40'}`}>
                  <Hand className="w-4 h-4" />{t('stopScreen.touchAnywhere', 'Touch Anywhere')}
                </button>
                <button type="button" onClick={() => set({ show_cta: true, touch_mode: 'button_only' })}
                  className={`h-11 rounded-xl border text-sm font-medium flex items-center justify-center gap-2 transition-colors ${cta.show_cta ? 'bg-foreground text-background border-foreground' : 'border-border text-muted-foreground hover:bg-muted/40'}`}>
                  <MousePointerClick className="w-4 h-4" />{t('stopScreen.touchButtonMode', 'Button')}
                </button>
              </div>
            </div>

            {/* Button-only extras — text + position. Hidden for Touch Anywhere. */}
            {cta.show_cta && (
              <div className="space-y-3">
                <TextInput label={t('stopScreen.ctaText', 'Button text')} value={cta.cta_text}
                  onChange={v => set({ cta_text: v })}
                  onBlur={() => set({ cta_text: cta.cta_text.trim() || 'Touch screen to preview menu' })}
                  maxLength={120} lang="any" />
                <div className="space-y-1.5">
                  <p className="text-sm font-medium">{t('stopScreen.ctaPosition', 'Button position')}</p>
                  <div className="grid grid-cols-3 gap-1.5 w-32">
                    {CTA_GRID.flat().map(pos => (
                      <button key={pos} type="button" onClick={() => set({ cta_position: pos })}
                        aria-label={pos}
                        className={`h-9 rounded-md border transition-colors ${cta.cta_position === pos ? 'bg-primary border-primary' : 'border-border hover:bg-muted/40'}`}>
                        <span className={`block w-1.5 h-1.5 rounded-full mx-auto ${cta.cta_position === pos ? 'bg-primary-foreground' : 'bg-muted-foreground/40'}`} />
                      </button>
                    ))}
                  </div>
                </div>

                {/* ── Button appearance ─────────────────────────────────── */}
                <div className="space-y-1.5">
                  <p className="text-sm font-medium">{t('stopScreen.ctaType', 'Button type')}</p>
                  <div className="flex flex-wrap gap-1.5">
                    {CTA_VARIANTS.map(v => (
                      <Chip key={v} active={cta.cta_variant === v} onClick={() => set({ cta_variant: v })}>
                        {t(`stopScreen.variant.${v}`, v)}
                      </Chip>
                    ))}
                  </div>
                </div>

                <div className="space-y-1.5">
                  <p className="text-sm font-medium">{t('stopScreen.ctaStyle', 'Style')}</p>
                  <div className="flex flex-wrap gap-1.5">
                    {CTA_STYLES.map(s => (
                      <Chip key={s} active={cta.cta_style === s} onClick={() => set({ cta_style: s })}>
                        {t(`stopScreen.style.${s}`, s)}
                      </Chip>
                    ))}
                  </div>
                </div>

                <div className="space-y-1.5">
                  <p className="text-sm font-medium">{t('stopScreen.ctaSize', 'Button size')}</p>
                  <div className="flex flex-wrap gap-1.5">
                    {CTA_SIZES.map(s => (
                      <Chip key={s} active={cta.cta_size === s} onClick={() => set({ cta_size: s })}>
                        {t(`stopScreen.size.${s}`, CTA_SIZE_LABEL[s])}
                      </Chip>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <ColorInput label={t('stopScreen.ctaTextColor', 'Text color')}
                    value={cta.cta_text_color} onChange={v => set({ cta_text_color: v })} />
                  <ColorInput label={t('stopScreen.ctaBgColor', 'Background color')}
                    value={cta.cta_bg_color} onChange={v => set({ cta_bg_color: v })} />
                </div>
              </div>
            )}
          </div>
            )
          })()}
            </TabsContent>
          </div>
        </div>

        {/* ── Right — preview (shared with the playlist page). bg-card panel;
            the CTA button is painted on the frame at its configured position,
            live — reflects the unsaved draft, not just the last Save, so the
            preview always matches what's currently configured on-screen. ── */}
        <div className="flex flex-col min-h-0 p-6 bg-card rounded-2xl border border-border shadow-sm overflow-hidden">
          <PreviewPanel
            mode="visual"
            items={attractItems}
            overlay={ctaDraft?.show_cta ? <CtaOverlay data={ctaDraft} /> : undefined}
          />
        </div>
      </div>

      <PlaylistContentPicker
        open={pickerOpen}
        removable
        tabs={['media', 'playlists']}
        excludeIds={data.media.map(m => m.id)}
        excludePlaylistIds={data.playlists.map(p => p.id)}
        onClose={() => setPickerOpen(false)}
        onConfirm={handleSetContent}
      />

      <PublishDialog<StopScreenData>
        open={publishMode !== null}
        mode={publishMode ?? 'publish'}
        kind="stop_screen"
        publish={payload => publishStopScreen(data.id, payload)}
        onClose={() => setPublishMode(null)}
        onPublished={updated => { setData(updated); setPublishMode(null) }}
      />

      <ConfirmDialog
        open={deleteOpen}
        title={t('stopScreen.deleteTitle', 'Delete Stop Screen')}
        description={t('stopScreen.deleteDesc', 'This stop screen will be removed. The media and playlists it references are not deleted.')}
        onConfirm={handleDelete}
        onClose={() => setDeleteOpen(false)}
      />
    </Tabs>
  )
}
